import axios from '@/libs/axios'

const auditBase = `${process.env.VUE_APP_URL}/api/v1/audit`

// Local calendar day as YYYY-MM-DD (avoids the UTC shift of toISOString()).
const todayStr = () => {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const defaultFilters = () => ({
  company: null,
  actor: null,
  actions: [],
  targetModel: null,
  targetId: null,
  targetRef: null,
  dateFrom: todayStr(),
  dateTo: todayStr(),
  search: '',
  page: 1,
  perPage: 25,
  sortBy: 'timestamp',
  sortDir: 'desc',
})

const defaultUsageFilters = () => {
  const to = new Date()
  const from = new Date()
  from.setDate(to.getDate() - 30)
  return {
    company: null,
    user: null,
    groupBy: 'user',
    source: 'all',
    dateFrom: from.toISOString().slice(0, 10),
    dateTo: to.toISOString().slice(0, 10),
  }
}

const buildEntriesParams = (f, companyId) => {
  const params = {
    company: companyId,
    page: f.page,
    page_size: f.perPage,
    ordering: `${f.sortDir === 'desc' ? '-' : ''}${f.sortBy}`,
  }
  if (f.actor) params.actor = f.actor
  if (f.actions && f.actions.length === 1) params.action = f.actions[0]
  if (f.actions && f.actions.length > 1) params.action__in = f.actions.join(',')
  if (f.targetModel) params.target_model = f.targetModel
  if (f.targetId) params.target_id = f.targetId
  if (f.targetRef) params.target_ref = f.targetRef
  if (f.dateFrom) params.timestamp_after = f.dateFrom
  if (f.dateTo) params.timestamp_before = f.dateTo
  if (f.search) params.search = f.search
  return params
}

const buildUsageParams = (f, companyId) => {
  const params = { company: companyId, group_by: f.groupBy, source: f.source }
  if (f.user) params.user = f.user
  if (f.dateFrom) params.period_from = f.dateFrom
  if (f.dateTo) params.period_to = f.dateTo
  return params
}

export default {
  namespaced: true,
  state: {
    entries: { count: 0, results: [], loading: false, error: null },
    usage: { totals: null, buckets: [], periodFrom: null, periodTo: null, loading: false, error: null },
    filters: defaultFilters(),
    usageFilters: defaultUsageFilters(),
    selectedEntry: null,
    entriesAbort: null,
    usageAbort: null,
  },
  mutations: {
    SET_ENTRIES_LOADING(state, v) { state.entries.loading = v },
    SET_ENTRIES(state, { count, results }) {
      state.entries.count = count
      state.entries.results = results
      state.entries.error = null
    },
    SET_ENTRIES_ERROR(state, err) { state.entries.error = err },
    SET_USAGE_LOADING(state, v) { state.usage.loading = v },
    SET_USAGE(state, payload) {
      state.usage.totals = payload.totals
      state.usage.buckets = payload.buckets
      state.usage.periodFrom = payload.period_from
      state.usage.periodTo = payload.period_to
      state.usage.error = null
    },
    SET_USAGE_ERROR(state, err) { state.usage.error = err },
    SET_FILTERS(state, patch) { state.filters = { ...state.filters, ...patch } },
    SET_USAGE_FILTERS(state, patch) { state.usageFilters = { ...state.usageFilters, ...patch } },
    RESET_FILTERS(state) { state.filters = defaultFilters() },
    RESET_USAGE_FILTERS(state) { state.usageFilters = defaultUsageFilters() },
    SET_SELECTED_ENTRY(state, entry) { state.selectedEntry = entry },
    SET_ENTRIES_ABORT(state, controller) { state.entriesAbort = controller },
    SET_USAGE_ABORT(state, controller) { state.usageAbort = controller },
  },
  actions: {
    async fetchEntries({ state, commit, rootGetters }) {
      const company = rootGetters['workspace/getCompany']
      const companyId = company && company.company
      if (!companyId) return
      if (state.entriesAbort) state.entriesAbort.abort()
      const controller = new AbortController()
      commit('SET_ENTRIES_ABORT', controller)
      commit('SET_ENTRIES_LOADING', true)
      commit('SET_ENTRIES_ERROR', null)
      try {
        const { data } = await axios.get(`${auditBase}/entries/`, {
          params: buildEntriesParams(state.filters, companyId),
          signal: controller.signal,
        })
        commit('SET_ENTRIES', { count: data.count, results: data.results })
      } catch (err) {
        if (axios.isCancel && axios.isCancel(err)) return
        if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return
        const status = err.response && err.response.status
        commit('SET_ENTRIES_ERROR', status === 403 ? 'forbidden' : 'network')
      } finally {
        commit('SET_ENTRIES_LOADING', false)
      }
    },
    async fetchUsage({ state, commit, rootGetters }) {
      const company = rootGetters['workspace/getCompany']
      const companyId = company && company.company
      if (!companyId) return
      if (state.usageAbort) state.usageAbort.abort()
      const controller = new AbortController()
      commit('SET_USAGE_ABORT', controller)
      commit('SET_USAGE_LOADING', true)
      commit('SET_USAGE_ERROR', null)
      try {
        const { data } = await axios.get(`${auditBase}/calculation-usage/`, {
          params: buildUsageParams(state.usageFilters, companyId),
          signal: controller.signal,
        })
        commit('SET_USAGE', data)
      } catch (err) {
        if (axios.isCancel && axios.isCancel(err)) return
        if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return
        const status = err.response && err.response.status
        commit('SET_USAGE_ERROR', status === 403 ? 'forbidden' : 'network')
      } finally {
        commit('SET_USAGE_LOADING', false)
      }
    },
  },
}
